import React from 'react';

type ProjectStatus = 'bostad' | 'hem' | 'värderad' | 'planerad' | 'väntar' | 'avslutad' | 'avbruten';
type ItemPriority = 'low' | 'medium' | 'high';
type PickupStatus = 'pending' | 'picked' | 'left';
type DamageStatus = 'open' | 'in-progress' | 'resolved';

type StatusType = ProjectStatus | ItemPriority | PickupStatus | DamageStatus;

// Text shades are -800 (light) / -300 (dark), not -600/-400.
// The badge fill is --bg-surface-alt, and on that background -600 fails WCAG AA
// badly: amber-600 is 2.61:1, emerald-600 3.09:1, red-600 3.95:1, blue-600
// 4.23:1. -800 measures 5.8-7.4:1 in light and -300 measures 6.1-8.0:1 in dark.
// The dot colours stay saturated — they are graphics (3:1), not text.
const STATUS_CONFIG: Record<string, { label: string; description?: string; dot: string; bg: string; text: string }> = {
  // Project statuses — the lifecycle a home moves through:
  //   Ny bostad -> Klar för hämtning -> Hämtning planerad -> Avslutad
  //
  // 'värderad' is named for what the PICKUP TEAM should do next, not for what
  // the valuator just finished — it is the state that puts a home into their
  // queue (the Sidebar already gives it a Truck icon).
  // (Väntar and Avbruten are off-path states.)
  //
  // These labels are the single source of truth. Previously the form and the
  // badge disagreed for the same status ('Klar för hämtning' vs 'Klar för
  // upphämtning', 'Planerad' vs 'Upphämtning planeras'), which made the
  // lifecycle hard to learn. `description` explains what the stage MEANS and is
  // shown where the user picks a status.
  bostad:   { label: 'Ny bostad',          description: 'Registrerad, inte värderad än', dot: 'bg-blue-500',   bg: 'bg-[var(--bg-surface-alt)]',   text: 'text-blue-800 dark:text-blue-300' },
  hem:      { label: 'Ny bostad',          description: 'Registrerad, inte värderad än', dot: 'bg-blue-500',   bg: 'bg-[var(--bg-surface-alt)]',   text: 'text-blue-800 dark:text-blue-300' },
  värderad: { label: 'Klar för hämtning',  description: 'Värderad – kan bokas in för hämtning', dot: 'bg-emerald-500', bg: 'bg-[var(--bg-surface-alt)]', text: 'text-emerald-800 dark:text-emerald-300' },
  planerad: { label: 'Hämtning planerad',  description: 'Datum satt för hämtteamet',     dot: 'bg-violet-500', bg: 'bg-[var(--bg-surface-alt)]', text: 'text-violet-800 dark:text-violet-300' },
  väntar:   { label: 'Väntar på svar',     description: 'Inväntar besked från kontakten', dot: 'bg-amber-500',  bg: 'bg-[var(--bg-surface-alt)]',  text: 'text-amber-800 dark:text-amber-300' },
  avslutad: { label: 'Avslutad',           description: 'Hämtad och klar',                dot: 'bg-on-surface-tertiary',  bg: 'bg-[var(--bg-surface-alt)]', text: 'text-on-surface-secondary' },
  avbruten: { label: 'Avbruten',           description: 'Genomförs inte',                 dot: 'bg-red-500',    bg: 'bg-[var(--bg-surface-alt)]',    text: 'text-red-800 dark:text-red-300' },
  // Item priority
  low:      { label: 'Låg',             dot: 'bg-emerald-500', bg: 'bg-[var(--bg-surface-alt)]', text: 'text-emerald-800 dark:text-emerald-300' },
  medium:   { label: 'Medium',          dot: 'bg-amber-500',  bg: 'bg-[var(--bg-surface-alt)]',  text: 'text-amber-800 dark:text-amber-300' },
  high:     { label: 'Hög',             dot: 'bg-red-500',    bg: 'bg-[var(--bg-surface-alt)]',    text: 'text-red-800 dark:text-red-300' },
  // Pickup status
  pending:  { label: 'Ej hämtad',       dot: 'bg-on-surface-tertiary',  bg: 'bg-[var(--bg-surface-alt)]', text: 'text-on-surface-secondary' },
  picked:   { label: 'Hämtad',          dot: 'bg-emerald-500', bg: 'bg-[var(--bg-surface-alt)]', text: 'text-emerald-800 dark:text-emerald-300' },
  left:     { label: 'Lämnad',          dot: 'bg-amber-500',  bg: 'bg-[var(--bg-surface-alt)]',  text: 'text-amber-800 dark:text-amber-300' },
  // Damage report status
  open:         { label: 'Öppen',       dot: 'bg-red-500',    bg: 'bg-[var(--bg-surface-alt)]',    text: 'text-red-800 dark:text-red-300' },
  'in-progress': { label: 'Pågående',   dot: 'bg-amber-500',  bg: 'bg-[var(--bg-surface-alt)]',  text: 'text-amber-800 dark:text-amber-300' },
  resolved:     { label: 'Löst',        dot: 'bg-emerald-500', bg: 'bg-[var(--bg-surface-alt)]', text: 'text-emerald-800 dark:text-emerald-300' },
};

interface StatusBadgeProps {
  status: StatusType;
  size?: 'sm' | 'md';
  showLabel?: boolean;
  className?: string;
}

export function StatusBadge({ status, size = 'sm', showLabel = true, className = '' }: StatusBadgeProps) {
  const config = STATUS_CONFIG[status] || { label: status, dot: 'bg-on-surface-tertiary', bg: 'bg-surface-alt', text: 'text-on-surface-secondary' };

  return (
    <span className={`inline-flex items-center gap-1.5 ${config.bg} ${config.text} ${
      size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm'
    } rounded-full font-medium ${className}`}>
      <span className={`${size === 'sm' ? 'w-1.5 h-1.5' : 'w-2 h-2'} rounded-full ${config.dot} flex-shrink-0`} />
      {showLabel && config.label}
    </span>
  );
}

export function StatusDot({ status, className = '' }: { status: StatusType; className?: string }) {
  const config = STATUS_CONFIG[status] || { dot: 'bg-on-surface-tertiary' };
  return <span className={`w-2.5 h-2.5 rounded-full ${config.dot} flex-shrink-0 ${className}`} />;
}

export function getStatusLabel(status: string): string {
  return STATUS_CONFIG[status]?.label || status;
}

/** Short plain-language explanation of what a status means. */
export function getStatusDescription(status: string): string | undefined {
  return STATUS_CONFIG[status]?.description;
}

export function getStatusColor(status: string): string {
  return STATUS_CONFIG[status]?.dot?.replace('bg-', '').replace('-500', '').replace('-400', '') || 'slate';
}

export { STATUS_CONFIG };
export type { ProjectStatus, StatusType };
